import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import DistributionGridApi from "../../../../../infrastructure/api/distributionGrid";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import { canManageDistributionTeam } from "../../../../utils/authRoleHelpers";
import EDCBreadcrumbs, { EDC_MANAGEMENT_CRUMB, EDC_ROOT_CRUMB } from "../../EDCBreadcrumbs";
import EDCFormDialog from "../../EDCFormDialog";
import EDCListCardHeader from "../../EDCListCardHeader";
import { EDC_PATHS } from "../../edcPaths";
import useDistributionPortalContext, { pick } from "../../useDistributionPortalContext";
import {
  INSTALLATION_KINDS,
  OWNERSHIP_KINDS,
  listOf,
  runDelete,
  toApiDate,
  toDateValue,
  toFieldValue,
  toNumberOrNull,
  useGridLookups,
} from "../grid/gridShared";

const emptyLVTrans = {
  id: null,
  code: "",
  name: "",
  capacityKva: "",
  feederId: null,
  distributionAreaId: "",
  installationKind: 0,
  ownershipKind: 0,
  address: "",
  latitude: "",
  longitude: "",
  actDate: null,
  isActive: true,
  notes: "",
};

const toForm = (t) => ({
  id: pick(t, "id", "Id"),
  code: pick(t, "code", "Code") || "",
  name: pick(t, "name", "Name") || "",
  capacityKva: toFieldValue(pick(t, "capacityKva", "CapacityKva")),
  feederId: pick(t, "feederId", "FeederId") ?? null,
  distributionAreaId: toFieldValue(pick(t, "distributionAreaId", "DistributionAreaId")),
  installationKind: Number(pick(t, "installationKind", "InstallationKind") ?? 0),
  ownershipKind: Number(pick(t, "ownershipKind", "OwnershipKind") ?? 0),
  address: pick(t, "address", "Address") || "",
  latitude: toFieldValue(pick(t, "latitude", "Latitude")),
  longitude: toFieldValue(pick(t, "longitude", "Longitude")),
  actDate: toDateValue(pick(t, "actDate", "ActDate")),
  isActive: pick(t, "isActive", "IsActive") !== false,
  notes: pick(t, "notes", "Notes") || "",
});

const validateForm = (form) => {
  if (!String(form.code || "").trim()) return "کد ترانس الزامی است";
  const capacity = toNumberOrNull(form.capacityKva);
  if (!capacity || capacity <= 0) return "ظرفیت ترانس (کیلوولت‌آمپر) را وارد کنید";
  return null;
};

const EDCLVTransPage = () => {
  const { context, companyId, companyName, isCompanyAdmin, setSelectedCompanyId, loading, error } =
    useDistributionPortalContext();
  const { lookups, reloadLookups, titleById } = useGridLookups(companyId, isCompanyAdmin);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [areaFilter, setAreaFilter] = useState("");
  const [feederFilter, setFeederFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadItems = useCallback(async () => {
    if (!companyId || !isCompanyAdmin) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    try {
      setItems(listOf(await DistributionGridApi.listLVTrans(companyId)));
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت ترانس‌های توزیع");
    } finally {
      setItemsLoading(false);
    }
  }, [companyId, isCompanyAdmin]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const feederOptions = useMemo(
    () =>
      lookups.feeders.map((f) => ({
        ...f,
        label: `${f.title} — ${titleById.substation.get(Number(f.parentId)) || "بدون ایستگاه"}`,
      })),
    [lookups.feeders, titleById]
  );

  const rows = useMemo(
    () =>
      items
        .map((t) => ({
          raw: t,
          id: pick(t, "id", "Id"),
          code: pick(t, "code", "Code") || "—",
          name: pick(t, "name", "Name") || "",
          feederId: pick(t, "feederId", "FeederId"),
          feederName: pick(t, "feederName", "FeederName") || "",
          substationName: pick(t, "substationName", "SubstationName") || "",
          distributionAreaId: pick(t, "distributionAreaId", "DistributionAreaId"),
          areaName: pick(t, "distributionAreaName", "DistributionAreaName") || "",
          capacityKva: Number(pick(t, "capacityKva", "CapacityKva") || 0),
          installationKindTitle: pick(t, "installationKindTitle", "InstallationKindTitle") || "",
          ownershipKindTitle: pick(t, "ownershipKindTitle", "OwnershipKindTitle") || "",
          isActive: pick(t, "isActive", "IsActive") !== false,
        }))
        .filter((r) => {
          if (areaFilter && String(r.distributionAreaId ?? "") !== areaFilter) return false;
          if (feederFilter === "none" && r.feederId) return false;
          if (feederFilter && feederFilter !== "none" && String(r.feederId) !== feederFilter) return false;
          if (statusFilter === "active" && !r.isActive) return false;
          if (statusFilter === "inactive" && r.isActive) return false;
          return true;
        }),
    [items, areaFilter, feederFilter, statusFilter]
  );

  const headCells = [
    { id: "code", label: "کد ترانس" },
    { id: "name", label: "نام / محل", render: (v) => v || "—" },
    {
      id: "feederName",
      label: "فیدر / ایستگاه",
      render: (v, row) => (v ? `${v}${row.substationName ? ` — ${row.substationName}` : ""}` : "—"),
    },
    { id: "areaName", label: "امور", render: (v) => v || "—" },
    { id: "capacityKva", label: "ظرفیت (kVA)", render: (v) => (v ? v.toLocaleString("fa-IR") : "—") },
    { id: "installationKindTitle", label: "نوع نصب", type: "chip" },
    { id: "ownershipKindTitle", label: "مالکیت" },
    {
      id: "isActive",
      label: "وضعیت",
      type: "boolean",
      model: { active: "فعال", deActive: "غیرفعال" },
    },
  ];

  const setField = (field, value) => setDialog((s) => ({ ...s, [field]: value }));

  const save = async () => {
    if (!companyId || !dialog) return;
    const validation = validateForm(dialog);
    if (validation) {
      toast.error(validation);
      return;
    }
    setSaving(true);
    try {
      await DistributionGridApi.saveLVTrans({
        id: dialog.id || 0,
        distributionCompanyId: Number(companyId),
        distributionAreaId: toNumberOrNull(dialog.distributionAreaId),
        feederId: dialog.feederId || null,
        code: dialog.code.trim(),
        name: dialog.name?.trim() || null,
        capacityKva: toNumberOrNull(dialog.capacityKva),
        installationKind: Number(dialog.installationKind) || 0,
        ownershipKind: Number(dialog.ownershipKind) || 0,
        address: dialog.address?.trim() || null,
        latitude: toNumberOrNull(dialog.latitude),
        longitude: toNumberOrNull(dialog.longitude),
        actDate: toApiDate(dialog.actDate),
        isActive: Boolean(dialog.isActive),
        notes: dialog.notes?.trim() || null,
      });
      toast.success(dialog.id ? "تغییرات ترانس ذخیره شد" : "ترانس ایجاد شد");
      setDialog(null);
      await loadItems();
    } catch (e) {
      toast.error(e?.message || "خطا در ذخیره ترانس");
    } finally {
      setSaving(false);
    }
  };

  const refresh = () => Promise.all([loadItems(), reloadLookups()]);

  if (!canManageDistributionTeam()) return <Navigate to={EDC_PATHS.root} replace />;
  if (context && !isCompanyAdmin) return <Navigate to={EDC_PATHS.root} replace />;

  const busy = loading || itemsLoading;
  const selectedFeeder = feederOptions.find((f) => f.id === dialog?.feederId) || null;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <EDCBreadcrumbs
          crumbs={[EDC_ROOT_CRUMB, EDC_MANAGEMENT_CRUMB, { title: "ترانس‌های توزیع", to: EDC_PATHS.lvTrans }]}
        />
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <Card>
          <EDCListCardHeader
            title="ترانس‌های توزیع"
            subheader={
              companyName
                ? `${companyName} — ${items.length} ترانس`
                : "ترانسفورماتورهای توزیع (۲۰ کیلوولت به ۴۰۰ ولت)"
            }
            context={context}
            onCompanyChange={setSelectedCompanyId}
            onCreate={() => setDialog({ ...emptyLVTrans })}
            createDisabled={!companyId}
            onRefresh={refresh}
            busy={busy}
          />
          <CardContent>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="امور"
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">همه امور</MenuItem>
                {lookups.areas.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="فیدر"
                value={feederFilter}
                onChange={(e) => setFeederFilter(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">همه فیدرها</MenuItem>
                <MenuItem value="none">بدون فیدر</MenuItem>
                {feederOptions.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="وضعیت"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="active">فعال</MenuItem>
                <MenuItem value="inactive">غیرفعال</MenuItem>
              </TextField>
            </Box>

            <EnhancedTable
              rowKey="id"
              rows={rows}
              headCells={headCells}
              pageSize={100}
              loading={busy}
              defaultSort={{ orderBy: "code", order: "asc" }}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: (row) => setDialog(toForm(row.raw)),
                },
                {
                  icon: <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "حذف",
                  color: "error",
                  onClick: (row) => setDeleteTarget(row),
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>

      <EDCFormDialog
        open={Boolean(dialog)}
        title={dialog?.id ? "ویرایش ترانس توزیع" : "ایجاد ترانس توزیع"}
        saving={saving}
        onClose={() => setDialog(null)}
        onSave={save}
      >
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={700}>
            مشخصات ترانس
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="کد ترانس"
            value={dialog?.code || ""}
            onChange={(e) => setField("code", e.target.value)}
            required
            fullWidth
            helperText="در هر شرکت یکتا"
            inputProps={{ dir: "ltr", maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="نام / محل ترانس"
            value={dialog?.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            fullWidth
            inputProps={{ maxLength: 200 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="ظرفیت (کیلوولت‌آمپر)"
            type="number"
            value={dialog?.capacityKva ?? ""}
            onChange={(e) => setField("capacityKva", e.target.value)}
            required
            fullWidth
            inputProps={{ min: 0, step: "any", dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="نوع نصب"
            value={dialog?.installationKind ?? 0}
            onChange={(e) => setField("installationKind", Number(e.target.value))}
            fullWidth
          >
            {INSTALLATION_KINDS.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="نوع مالکیت"
            value={dialog?.ownershipKind ?? 0}
            onChange={(e) => setField("ownershipKind", Number(e.target.value))}
            fullWidth
          >
            {OWNERSHIP_KINDS.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <JalaliDatePicker
            label="تاریخ نصب / بهره‌برداری"
            value={dialog?.actDate || null}
            onChange={(v) => setField("actDate", toDateValue(v))}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            اتصال به شبکه
          </Typography>
        </Grid>
        <Grid item xs={12} md={7}>
          <Autocomplete
            options={feederOptions}
            value={selectedFeeder}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => setField("feederId", v?.id ?? null)}
            noOptionsText="فیدری یافت نشد"
            renderInput={(params) => <TextField {...params} label="فیدر تغذیه‌کننده" />}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            select
            label="امور"
            value={dialog?.distributionAreaId ?? ""}
            onChange={(e) => setField("distributionAreaId", e.target.value)}
            fullWidth
            helperText="خالی = امور فیدر"
          >
            <MenuItem value="">بدون امور</MenuItem>
            {lookups.areas.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            موقعیت
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="آدرس"
            value={dialog?.address || ""}
            onChange={(e) => setField("address", e.target.value)}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="عرض جغرافیایی"
            type="number"
            value={dialog?.latitude ?? ""}
            onChange={(e) => setField("latitude", e.target.value)}
            fullWidth
            inputProps={{ step: "any", dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="طول جغرافیایی"
            type="number"
            value={dialog?.longitude ?? ""}
            onChange={(e) => setField("longitude", e.target.value)}
            fullWidth
            inputProps={{ step: "any", dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="توضیحات"
            value={dialog?.notes || ""}
            onChange={(e) => setField("notes", e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch checked={dialog?.isActive !== false} onChange={(e) => setField("isActive", e.target.checked)} />
            }
            label={dialog?.isActive !== false ? "ترانس در مدار است" : "ترانس از مدار خارج است"}
          />
        </Grid>
      </EDCFormDialog>

      <DeleteConfirm
        open={Boolean(deleteTarget)}
        message={`ترانس «${deleteTarget?.code || ""}» حذف شود؟`}
        onClose={(loadAgain) => {
          setDeleteTarget(null);
          if (loadAgain === true) refresh();
        }}
        onSubmit={() =>
          runDelete(
            () => DistributionGridApi.deleteLVTrans({ distributionCompanyId: Number(companyId), id: deleteTarget.id }),
            "ترانس حذف شد"
          )
        }
      />
    </Grid>
  );
};

export default EDCLVTransPage;
